import { all, dropTask } from 'ember-concurrency';
import Button from 'tio-reimagined/components/button';
import { currencyStringToNumber } from 'tio-common/utils/format';
import Divider from 'tio-reimagined/components/divider';
import { flatten, safeParse } from 'valibot';
import { fn } from '@ember/helper';
import { Header, HStack, Section, VStack } from 'tio-reimagined/components/layout';
import Input from 'tio-reimagined/components/input';
import ReSelect from 'tio-reimagined/components/select';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { and, eq, join, not, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { setSemesterCodeString } from 'tio-common/utils/format';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
  setSingleValueForTasField,
  getConfigForTasField,
  getFieldLabelForFieldName,
  isTasFieldComponentType,
  type FieldName,
  type FieldValue,
  type TemplateFieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { LinkTo } from '@ember/routing';
import { trackedObject } from '@ember/reactive/collections';
import { capitalizeWords } from 'tio-employee/utils/tas/textUtilities';
import { courseCashApplicationPage } from 'tio-common/utils/tours/course-cash-tour';
import Text from 'tio-reimagined/components/text';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import ActionableList from 'tio-ui/components/actionable-list';
import AddEducationDrawer from 'tio-employee/components/tas/add-education-drawer';
import AddScholarshipsDrawer from 'tio-common/components/tas/add-scholarships-drawer';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import { divide } from 'tio-ui/math';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import inputmask from 'tio-common/modifiers/inputmask';
import { getInputmaskAlias, getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import LimitsSummary from 'tio-common/components/tuition-assistance/limits-summary';
import ProgramFormField from 'tio-common/components/tuition-assistance/program-form-field';
import ReimbursementCalculationIfApplicable from 'tio-common/components/tuition-assistance/reimbursement-calculation-if-applicable';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import SchoolSelect from 'tio-common/components/tuition-assistance/program-details/school-select';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import TuitionAssistanceFormsCustomFieldsForProvince from 'tio-common/components/tuition-assistance/forms/custom-fields-for-province';
import TourTrigger from 'tio-common/components/tours/tour-trigger';
import type {
  CustomFieldSignature,
  ReceivedScholarship,
} from 'tio-common/types/tuition-assistance';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/models/tas-program-instance';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/models/tas-program-template';
import type { Touched } from 'tio-common/helpers/errors-for-valibot-field';
import type ConditionModel from 'tio-data/models/condition';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationAdapter from 'tio-employee/adapters/tas-application';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasParticipantAdapter from 'tio-employee/adapters/tas-participant';
import type TasCourseModel from 'tio-data/models/tas-course';
import { abandonProgramRequest } from 'tio-common/requests/tas-program-instance';
import { buildTasEmployeeActor } from 'tio-common/utils/tas-actor';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasProgramTemplateInstitutionModel from 'tio-data/models/tas-program-template-institution';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import type TasUnifiedInstitutionModel from 'tio-data/models/tas-unified-institution';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type Owner from '@ember/owner';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type AgreementModel from 'tio-data/models/agreement';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import PaymentMethodAndSubtotals from '../../payment-method-and-subtotals';
import ReimbursementPopoverTag from 'tio-common/components/tas/reimbursement-popover-tag';
import type IntlService from 'ember-intl/services/intl';
import {
  PhCalendarBlank,
  PhCaretLeft,
  PhCheckCircle,
  PhCircle,
  PhClipboardText,
  PhInfo,
  PhLightbulb,
  PhNotePencil,
  PhPencil,
  PhPlus,
  PhTrash,
  PhXCircle,
} from 'ember-phosphor-icons';
import Checkbox from 'tio-reimagined/components/checkbox';
import type LpnPage from 'tio-data/models/lpn-page';
import type PartnerService from 'tio-employee/services/partner';
import { normalizeDate } from 'tio-common/utils/date/normalize-date';
import type { TasUnifiedInstitution } from 'tio-data/models/search';
import type TourManager from 'tio-common/services/tour-manager';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { formatDateToISO } from 'tio-common/utils/date';

type ProgramInstanceFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FormField = {
  name: ProgramInstanceFieldName;
  mask: string | null;
  type: string | null;
  rules: {
    type: string;
    typeError?: string;
  };
  disabled: boolean;
  fieldOrigination: 'instance' | 'application';
};

export interface NewTypeTwoOrThreeSignature {
  Args: {
    model: {
      application: TasApplicationModel;
      programInstance: TASProgramInstanceModel;
      selectedSchool?: TasUnifiedInstitutionModel;
    };
  };
}

interface Scholarship extends ReceivedScholarship {
  id?: string;
}

export default class NewTypeTwoOrThreeComponent extends Component<NewTypeTwoOrThreeSignature> {
  programTemplate: TasProgramTemplateModel;
  instanceFields: TASProgramInstanceModelFieldsSignature;

  constructor(owner: Owner, args: NewTypeTwoOrThreeSignature['Args']) {
    super(owner, args);
    const { application } = args.model;
    this.programTemplate = application.tasProgramInstance.tasProgramTemplate;

    // Set default payment method BEFORE creating trackedObject to avoid tracking errors
    if (this.programTemplate.isDirectPayProgram) {
      const existingMethod = getSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        application.fields,
        ''
      );
      if (!existingMethod) {
        setSingleValueForTasField(
          'PAYMENT_TYPE_FOR_DIRECT_PAY',
          'TAS.AssistanceManner.COURSE_CASH',
          application.fields
        );
      }
    }

    if (this.showGPAField) {
      this.setGPA(getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || '0');
    }
    if (this.requiresDateEligibilityCheck) {
      this.updateEligibility();
    }
    this.setSelectedSchoolIfExists();
    this.instanceCustomFields = application.tasProgramInstance.customFields;
    this.applicationCustomFields = application.customFields;
    this.instanceFields = trackedObject(application.tasProgramInstance.fields);
    this.applicationFields = trackedObject(application.fields);
    this.updateCustomRequiredFields();
    this.initializePaymentMethod();
    this.endDate = getSingleValueForTasField('COURSES_END_DATE', this.applicationFields, '');
    this.startDate = getSingleValueForTasField('COURSES_BEGIN_DATE', this.applicationFields, '');
    this.initializeLimitsIfNeeded();
    this.registerTours();
    if (!!this.startDate) {
      this.touched = { dateEntered: true };
    } else {
      this.touched = { dateEntered: false };
    }
  }

  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked course: TasCourseModel | null = null;
  @tracked eligibility = true;
  @tracked hasSubmitted = false;
  @tracked isDeleteApplicationOpen = false;
  @tracked isDeleteCourseOpen = false;
  @tracked isDeleteScholarshipOpen = false;
  @tracked isEditScholarshipOpen = false;
  @tracked isEducationOpen = false;
  @tracked isOpen = false;
  @tracked isScholarshipOpen = false;
  @tracked scholarshipEditError = '';
  @tracked scholarshipIdToEdit?: string;
  @tracked scholarshipToDelete?: Scholarship;
  @tracked scholarshipToEdit?: Scholarship;
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked submitError: string | null = null;
  @tracked instanceCustomFields: CustomFieldSignature[] = [];
  @tracked applicationCustomFields: CustomFieldSignature[] = [];
  @tracked gpaBasedAmount = 0;
  @tracked requiredCustomFieldsFilled = false;
  @tracked highlightedSection: string | null = null;
  @tracked paymentMethod = 'TAS.AssistanceManner.REIMBURSEMENT';
  @tracked showPaymentMethodModal = false;
  @tracked isLpnSchoolSelected = false;
  @tracked endDate = '';
  @tracked startDate = '';
  applicationFields!: TasApplicationModel['fields'];
  @tracked touched: Touched = { dateEntered: false };
  @tracked shouldFetchLimits = false;

  @tracked dynamicEligibility?: boolean;
  @tracked programEligibility: [string, string][] = [];

  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare partner: PartnerService;
  @service declare tourManager: TourManager;
  @service declare betaExperience: BetaExperienceService;

  // Type guards for TasUnifiedInstitution properties
  private hasValidDapipId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { dapipId: string } {
    return !!(institution && 'dapipId' in institution && institution.dapipId);
  }

  private hasValidInstitutionId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { institutionId: string } {
    return !!(institution && 'institutionId' in institution && institution.institutionId);
  }

  private hasValidLocationName(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { locationName: string } {
    return !!(institution && 'locationName' in institution && institution.locationName);
  }

  private registerTours() {
    if (this.programTemplate.isDirectPayProgram) {
      // pass the function to open the education drawer to the tour steps
      this.tourManager.registerTour(courseCashApplicationPage(this.openEducationDrawer));
    }
  }

  initializeLimitsIfNeeded() {
    const basedOn = this.programTemplate.calculateTowardsLimitBasedOn;
    const hasSetDate =
      (basedOn === 'COURSE_START_DATE' && !!this.startDate) ||
      (basedOn === 'COURSE_END_DATE' && !!this.endDate);

    if (!hasSetDate) return;
    this.shouldFetchLimits = true;
  }

  calculateEligibility() {
    if (!this.programEligibility.length) {
      return false;
    }
    // Use today's date for 'submission' lock status, otherwise use the provided start or end date.
    const enteredDate =
      this.lockStatus === 'submission' ? new Date() : new Date(this.startOrEndDate);

    return this.programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  }

  async updateEligibility(): Promise<void> {
    // Don't check eligibility if the application is in ignore policies mode
    if (
      this.application.ignorePolicies ||
      this.lockStatus === 'fulfilled' ||
      !this.lockStatus ||
      !this.application.applicationSubmittedDate
    ) {
      this.eligibility = true;
      return;
    }
    try {
      if (!this.dynamicEligibility || !this.programEligibility.length) {
        const participantId = this.instance.employee.tasParticipant?.id;
        const { code } = this.programTemplate;

        if (!participantId) {
          // No participant record ⇒ no eligibility can be determined.
          this.eligibility = false;
          return;
        }

        const participantAdapter = this.store.adapterFor(
          'tas-participant'
        ) as unknown as TasParticipantAdapter;
        // Passing a program code makes the adapter return the per-program value
        // (boolean / [string,string][]) rather than the full map, but it can
        // still be undefined if the key is missing. Normalise to safe defaults.
        const [fetchDynamicEligibility, fetchProgramEligibility] = await Promise.all([
          participantAdapter.dynamicEligibility(participantId, code),
          participantAdapter.periodOfEligibility(participantId, code),
        ]);

        this.dynamicEligibility = Boolean(fetchDynamicEligibility);
        this.programEligibility = Array.isArray(fetchProgramEligibility)
          ? (fetchProgramEligibility as [string, string][])
          : [];
      }

      this.eligibility = this.dynamicEligibility! && this.calculateEligibility();
    } catch (error) {
      console.error('Error fetching eligibility:', error);
      this.eligibility = false;
    }
  }

  setSelectedSchoolIfExists() {
    if (this.args.model.selectedSchool) {
      this.selectedSchool = this.args.model.selectedSchool;
    }
  }

  updateDateFieldValue = (fieldName: FieldName, selectedDates: Date[]) => {
    const value =
      selectedDates.length > 0 && selectedDates[0] ? formatDateToISO(selectedDates[0]) : '';
    // @ts-expect-error - FieldName not fully compatible with setSingleValueForTasField signature
    setSingleValueForTasField(fieldName, value, this.instanceFields);
  };

  get programFormFieldsForTypeTwoAndThree(): FormField[] {
    const numberTypeError = 'Value must be a number';
    return [
      {
        name: 'APPLICATION_NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'APPLICATION_ID',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'EMPLOYEE_ID',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'FT_PT_OTHER',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'EMPLOYEE_DOB',
        mask: null,
        type: 'date',
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'EMPLOYEE_EMAIL',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'SCHOOL_INSTITUTION_NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'PROGRAM_SPECIALIZATION',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'PROGRAM_MAJOR',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'ESTIMATED_COST',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'ENROLLMENT_OBJECTIVE',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'STUDENT_ID',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'application',
      },
      {
        name: 'ESTIMATED_PROGRAM_BEGIN',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'ESTIMATED_PROGRAM_COMPLETION',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
        fieldOrigination: 'instance',
      },
    ];
  }

  get application() {
    return this.args.model.application;
  }

  get instance() {
    return this.args.model.programInstance;
  }

  get applicationName() {
    return getSingleValueForTasField('APPLICATION_NAME', this.applicationFields);
  }

  get educationSectionTitle() {
    return this.application.expenseType || 'Education';
  }

  get typeClassification() {
    return this.programTemplate.typeClassification || '';
  }

  get isTypeTwoProgram() {
    return this.typeClassification === 'TAS.ProgramType.2';
  }

  get conditionsForSubmit() {
    const conditionsMap = {
      'TAS.ProgramType.2': this.programTemplate.typeTwoApplicationConditions,
      'TAS.ProgramType.3': this.programTemplate.applicationApproveCoursesConditions,
    };

    return conditionsMap[this.typeClassification as keyof typeof conditionsMap] || [];
  }

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return agreement.save();
    });
    const saved = await all(promises);
    return saved;
  });

  get shouldShowAttachments() {
    if (this.isTypeTwoProgram) {
      return (
        this.programTemplate.attachmentsForCourseApprovalVisible ||
        this.programTemplate.attachmentsForCourseCompletionVisible
      );
    }
    return this.programTemplate.attachmentsForCourseApprovalVisible;
  }

  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    if (this.isTypeTwoProgram) {
      return (
        this.programTemplate.requireAttachmentsForCourseApproval ||
        this.programTemplate.requireAttachmentsForCourseCompletion
      );
    }
    return this.programTemplate.requireAttachmentsForCourseApproval;
  }

  get attachmentClassificationString() {
    return this.isTypeTwoProgram ? 'COMPLETION_DOCUMENTS' : 'COURSE_APPROVAL_DOCUMENTS';
  }

  get attachmentsListItems() {
    const { programApproval, coursePreApproval, courseCompletion } =
      this.programTemplate.attachmentListItems;

    const itemsToInclude = this.isTypeTwoProgram
      ? [programApproval, coursePreApproval, courseCompletion]
      : [programApproval, coursePreApproval];

    return itemsToInclude.flat();
  }

  get attachmentsListInstructions() {
    const { programApproval, coursePreApproval, courseCompletion } =
      this.programTemplate.attachmentInstructions;

    const itemsToInclude = this.isTypeTwoProgram
      ? [programApproval, coursePreApproval, courseCompletion].filter(Boolean)
      : [programApproval, coursePreApproval].filter(Boolean);

    return itemsToInclude.length ? itemsToInclude.join(', ') : '';
  }

  get lockStatus() {
    const lockType: Record<string, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.lockEmployeeStatusBasedOn]
      : '';
  }

  get expenseTypeField() {
    let expenseType = getSingleValueForTasField(
      'EXPENSE_TYPE',
      this.programTemplate.fields
    ) as string;
    return expenseType || 'Education';
  }

  get scholarships() {
    let savedScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED',
      this.application.fields
    );
    return savedScholarships.map((scholarship, index) => ({
      ...(scholarship as ReceivedScholarship),
      id: `scholarship-${index + 1}`,
    }));
  }

  get scholarshipName() {
    return this.programTemplate.fields['SCHOLARSHIP_NAME'];
  }

  get scholarshipAmount() {
    return this.programTemplate.fields['SCHOLARSHIP_AMOUNT'];
  }

  get scholarshipReceived() {
    return this.programTemplate.fields['SCHOLARSHIPS_RECEIVED'];
  }

  get customEmployeeFields(): CustomFieldSignature[] {
    return this.instanceCustomFields.filter((field) => field.province === 'EMPLOYEE');
  }

  get customProgramFields(): CustomFieldSignature[] {
    return this.instanceCustomFields.filter((field) => field.province === 'PROGRAM');
  }

  get customApplicationFields(): CustomFieldSignature[] {
    return this.applicationCustomFields.filter((field) => field.province === 'APPLICATION');
  }

  toggleLpnDiscountActivated = () => {
    const app = this.args.model.application;
    app.lpnDiscountActivated = !app.lpnDiscountActivated;
  };

  get isLpnEnabled() {
    const { lpn } = this.partner.companySettings;
    if (lpn && 'isEnabled' in lpn) {
      return lpn.isEnabled as boolean;
    }
    return false;
  }

  isSelectedSchoolLpn = async (searchResult: TasUnifiedInstitution) => {
    const lpnSettings = this.partner.companySettings.lpn;
    const isEnabled = lpnSettings?.isEnabled;
    if (!isEnabled) {
      this.isLpnSchoolSelected = false;
      return false;
    }

    let institutionIdentifier: string | undefined;
    if (this.hasValidDapipId(searchResult)) {
      institutionIdentifier = `dapip_${searchResult.dapipId}`;
    } else if (this.hasValidInstitutionId(searchResult)) {
      institutionIdentifier = `tas_${searchResult.institutionId}`;
    } else {
      this.isLpnSchoolSelected = false;
      return false;
    }

    const pages = await this.store.query<LpnPage>('lpn-page', {
      filter: { 'institution-identifier': institutionIdentifier },
      include: 'company-lpn-settings,company-lpn-settings.company-setting',
    });
    if (pages.length === 0) {
      this.isLpnSchoolSelected = false;
      return false;
    }
    const setting = await pages[0]?.settingForCompany(this.partner.companySettings.id);
    const enabled = !setting || setting.isEnabled;
    this.isLpnSchoolSelected = enabled;
    return enabled;
  };

  inputValueForField = (field: FieldName, programField: FormField) => {
    const fields =
      programField.fieldOrigination === 'instance' ? this.instanceFields : this.applicationFields;
    return getSingleValueForTasField(field, fields) as string;
  };

  featuredSchoolData = trackedFunction(this, async () => {
    const template = this.args.model.programInstance.tasProgramTemplate;
    const schools = await this.store.query<TasProgramTemplateInstitutionModel>(
      'tas-program-template-institution',
      {
        filter: {
          'is-featured': true,
          'tas-program-template': template.id,
        },
        include: 'accredited-institution,unaccredited-institution,logo',
      }
    );
    return schools;
  });

  get featuredSchools() {
    return this.featuredSchoolData.value || [];
  }

  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    if (!existingFields) {
      return updatedFields;
    }
    updatedFields.forEach((newObject) => {
      const index = existingFields.findIndex(
        (item) => item.label === newObject.label && item.province === newObject.province
      );
      existingFields.splice(index, 1, newObject);
    });

    return existingFields;
  };

  updateCustomRequiredFields = () => {
    let customFields = [...this.instanceCustomFields, ...this.applicationCustomFields];
    this.requiredCustomFieldsFilled = customFields.every((field) => {
      if (field.required) {
        return field.values[0] !== null && field.values[0] !== undefined && field.values[0] !== '';
      }
      return true;
    });
  };

  didUpdateApplicationCustomFields = async (customFields: CustomFieldSignature[]) => {
    let updatedFields = this.updateFieldsWithNewValues(this.applicationCustomFields, customFields);

    this.application.customFields = updatedFields;
    this.updateCustomRequiredFields();
    return this.updateFieldsWithNewValues(this.applicationCustomFields, updatedFields);
  };

  didUpdateEmployeeCustomFields = async (customFields: CustomFieldSignature[]) => {
    let updatedFields = this.updateFieldsWithNewValues(this.customEmployeeFields, customFields);

    this.instanceCustomFields = updatedFields;
    this.updateCustomRequiredFields();
    return this.updateFieldsWithNewValues(this.instanceCustomFields, updatedFields);
  };

  didUpdateProgramCustomFields = async (customFields: CustomFieldSignature[]) => {
    let updatedFields = this.updateFieldsWithNewValues(this.customProgramFields, customFields);

    this.instanceCustomFields = updatedFields;
    this.updateCustomRequiredFields();
    return this.updateFieldsWithNewValues(this.instanceCustomFields, updatedFields);
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  isSchoolSelectProgramField = (fieldName: ProgramInstanceFieldName) => {
    return isTasFieldComponentType(fieldName, this.programTemplate.fields, 'school-select');
  };

  didSelectSchool = async (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;

    if (!searchResult) {
      this.isLpnSchoolSelected = false;
      return;
    }
    const isLpnSchool = await this.isSelectedSchoolLpn(searchResult);
    if (isLpnSchool) {
      this.isLpnSchoolSelected = true;
    } else {
      this.isLpnSchoolSelected = false;
    }

    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.hasValidLocationName(searchResult) ? searchResult.locationName : '',
      this.instanceFields
    );
    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      this.hasValidInstitutionId(searchResult) ? searchResult.institutionId : '',
      this.instanceFields
    );
    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      this.hasValidDapipId(searchResult) ? searchResult.dapipId : '',
      this.instanceFields
    );
  };

  setGPA = (value: string) => {
    const gpa = Number(value);
    setSingleValueForTasField('GRADE_POINT_AVERAGE', gpa, this.applicationFields);
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
  };

  get lockEmployeeStatusBasedOn() {
    return this.programTemplate.lockEmployeeStatusBasedOn;
  }

  get requiresDateEligibilityCheck() {
    const locksRequired = [
      'COURSE_START_DATE',
      'COURSE_END_DATE',
      'ON_COURSES_SUBMISSION',
      'ON_SUBMISSION',
    ];
    return (
      locksRequired.includes(this.lockEmployeeStatusBasedOn) &&
      !this.application.applicationSubmittedDate
    );
  }

  setSemesterCodeStringOnApplication = () => {
    if (this.programTemplate.semesterCodeIsVisible) {
      const date = getSingleValueForTasField(
        'COURSES_BEGIN_DATE',
        this.applicationFields
      ) as string;
      if (!date) {
        return;
      }
      const semesterCode = setSemesterCodeString(date);
      setSingleValueForTasField('SEMESTER_CODE', semesterCode, this.applicationFields);
    }
  };

  saveStartDate = async (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      this.startDate = formatDateToISO(selectedDates[0]);
      setSingleValueForTasField('COURSES_BEGIN_DATE', this.startDate, this.applicationFields);
      this.touched = { dateEntered: true };

      if (this.programTemplate.calculateTowardsLimitBasedOn === 'COURSE_START_DATE') {
        this.application.fields = this.applicationFields;
        await this.application.save();
        this.shouldFetchLimits = true;
        if (!this.startDateValidationErrors.length) {
          // Refresh the limits status data when the start date changes
          this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
        }
      }
      if (this.lockEmployeeStatusBasedOn === 'COURSE_START_DATE') {
        this.updateEligibility();
      }
    } else {
      this.startDate = '';
      setSingleValueForTasField('COURSES_BEGIN_DATE', '', this.applicationFields);
    }
  };

  saveEndDate = async (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      this.endDate = formatDateToISO(selectedDates[0]);
      setSingleValueForTasField('COURSES_END_DATE', this.endDate, this.applicationFields);

      if (this.programTemplate.calculateTowardsLimitBasedOn === 'COURSE_END_DATE') {
        this.application.fields = this.applicationFields;
        await this.application.save();
        this.shouldFetchLimits = true;
        // Refresh the limits status data when the end date changes
        this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
      }

      if (this.lockEmployeeStatusBasedOn === 'COURSE_END_DATE') {
        this.updateEligibility();
      }
    } else {
      this.endDate = '';
      setSingleValueForTasField('COURSES_END_DATE', '', this.applicationFields);
    }
  };

  updateAppName = (value: string) => {
    setSingleValueForTasField('APPLICATION_NAME', value, this.applicationFields);
  };

  scrollTo = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.highlightedSection = sectionId;
    setTimeout(() => (this.highlightedSection = null), 2000);
  };

  openEducationDrawer = (course?: TasCourseModel | null) => {
    if (course) {
      this.course = course;
    } else {
      this.course = this.store.createRecord('tas-course', {
        tasApplication: this.application,
        fields: this.programTemplate.courseFieldsHash,
        customFields: this.programTemplate.courseCustomFields,
      });
    }
    this.isEducationOpen = true;
  };

  closeEducationDrawer = () => {
    this.course = null;
    this.isEducationOpen = false;
  };

  toggleScholarshipDrawer = () => {
    this.isScholarshipOpen = !this.isScholarshipOpen;
    if (!this.isScholarshipOpen) {
      this.scholarshipIdToEdit = undefined;
      // Sync local applicationFields with any scholarship changes saved in the drawer
      this.syncScholarshipsFromApplication();
    }
  };

  syncScholarshipsFromApplication() {
    // Update the local trackedObject with the latest scholarship data from application.fields
    if (this.application.fields['SCHOLARSHIPS_RECEIVED']) {
      this.applicationFields['SCHOLARSHIPS_RECEIVED'] =
        this.application.fields['SCHOLARSHIPS_RECEIVED'];
    }
  }

  get requiredFieldsSet() {
    const fieldsToCheck = [
      ...this.programFormFieldsForTypeTwoAndThree,
      { name: 'COURSES_BEGIN_DATE', disabled: false, fieldOrigination: 'application' },
      { name: 'COURSES_END_DATE', disabled: false, fieldOrigination: 'application' },
      { name: 'EMPLOYEE_DOB', disabled: false, fieldOrigination: 'instance' },
    ];

    return fieldsToCheck.every((field) => {
      const fieldConfig = getConfigForTasField(
        field.name as TemplateFieldName,
        this.programTemplate.fields
      );

      if (!fieldConfig.required || field?.disabled === true) {
        return true; // Skips non-required or disabled (pre-filled) fields
      }

      // Get value from the correct source based on fieldOrigination
      const fieldsSource =
        'fieldOrigination' in field && field.fieldOrigination === 'application'
          ? this.applicationFields
          : this.instanceFields;

      const value = getSingleValueForTasField(field.name as FieldName, fieldsSource);

      return value !== null && value !== undefined && value !== '';
    });
  }

  get hasRequiredAttachments() {
    let hasRequiredAttachments: boolean = false;
    switch (this.typeClassification) {
      case 'TAS.ProgramType.2':
        hasRequiredAttachments =
          this.application.hasRequiredEvidenceAttachments ||
          this.application.hasRequiredPreApprovalAttachments;
        break;
      case 'TAS.ProgramType.3':
        hasRequiredAttachments = this.application.hasRequiredPreApprovalAttachments;
        break;
      default:
        console.error(`Unhandled typeClassification: ${this.typeClassification}`);
        hasRequiredAttachments = false;
    }
    return hasRequiredAttachments;
  }

  get hasGpaIfRequired(): boolean {
    // checks if the GPA field is required
    if (!this.showGPAField) {
      return true;
    }

    const hasField = Boolean(
      getSingleValueForTasField('GRADE_POINT_AVERAGE', this.applicationFields)
    );

    return hasField;
  }

  get eligibleBasedOnWaitingPeriod() {
    return !this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.eligibilityWaitingPeriod,
      this.instance.employee?.tasParticipant,
      this.startDate || undefined
    );
  }

  get eligibilityWaitingPeriod() {
    return this.programTemplate.eligibilityWaitingPeriod as number;
  }

  get dateEligible() {
    const date = this.tuitionAssistance.waitingPeriodEndDate(
      this.eligibilityWaitingPeriod,
      this.instance.employee?.tasParticipant
    );
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get startDateValidationErrors() {
    const startDateConfig = getConfigForTasField('COURSES_BEGIN_DATE', this.programTemplate.fields);
    if (!startDateConfig.visible) {
      return {};
    }
    const schema = this.tuitionAssistance.dynamicBeginDateValidationSchema(
      this.programTemplate,
      this.endDate
    );
    const model = {
      dateEntered: new Date(this.startDate),
    };

    const result = safeParse(schema, model, { abortEarly: false });
    if (!result.success) {
      const flattened = flatten(result.issues);
      return flattened.nested ?? {};
    }
    return {};
  }

  get hasStartDateValidationErrors(): boolean {
    const errors = this.startDateValidationErrors;
    return Object.keys(errors || {}).length > 0;
  }

  get canSubmitApplication() {
    const requiredState = 'TAS.ApplicationState.DEFINE_COURSES';
    return (
      this.isActiveParticipant &&
      !!this.application.activeCourses.length &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      this.application.state === requiredState &&
      this.requiredFieldsSet &&
      this.eligibility &&
      this.requiredCustomFieldsFilled &&
      this.eligibleBasedOnWaitingPeriod &&
      this.hasGpaIfRequired && // TAS.TODO: rework when updating validation
      !this.hasStartDateValidationErrors
    );
  }

  get adapterMethod() {
    const actionMap = {
      'TAS.ProgramType.2': 'requestCourseEvidence',
      'TAS.ProgramType.3': 'requestCoursesApproval',
    };
    return actionMap[this.typeClassification as keyof typeof actionMap] || '';
  }

  get startOrEndDate(): string {
    if (this.lockStatus === 'start') {
      return getSingleValueForTasField('COURSES_BEGIN_DATE', this.applicationFields);
    }
    if (this.lockStatus === 'end') {
      return getSingleValueForTasField('COURSES_END_DATE', this.applicationFields);
    }
    return new Date().toLocaleDateString('en-CA');
  }

  get startDateConfig() {
    return getConfigForTasField('COURSES_BEGIN_DATE', this.programTemplate.fields);
  }

  get endDateConfig() {
    return getConfigForTasField('COURSES_END_DATE', this.programTemplate.fields);
  }

  get isClaimsFinancialsApplication() {
    return this.programTemplate.calculateTotalByClaimsFinancials;
  }

  get calculateTotalByGPA() {
    return this.programTemplate.calculateTotalByGPA;
  }

  get showGPAField() {
    return this.calculateTotalByGPA && this.isTypeTwoProgram;
  }

  get setRequestedTotalAmount() {
    if (this.isClaimsFinancialsApplication) {
      return 0;
    }

    if (!this.showGPAField) {
      return this.application.requestedTotal;
    }

    return Math.min(this.gpaBasedAmount, this.application.requestedTotal);
  }

  get approvedTotalString() {
    if (this.isTypeTwoProgram) {
      return 'FINAL_APPROVED_TOTAL';
    } else {
      return 'PRE_APPROVED_TOTAL';
    }
  }

  get startDateLabel() {
    return getFieldLabelForFieldName('COURSES_BEGIN_DATE', this.programTemplate.fields) as string;
  }

  get endDateLabel() {
    return getFieldLabelForFieldName('COURSES_END_DATE', this.programTemplate.fields) as string;
  }

  get isActiveParticipant() {
    return this.instance.employee?.tasParticipant?.isActive || false;
  }

  async prepareTotalsMiscFieldsAndSave() {
    this.setSemesterCodeStringOnApplication();

    // Save payment method for direct pay programs
    if (this.programTemplate.isDirectPayProgram) {
      setSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        this.paymentMethod,
        this.applicationFields
      );
    }

    if (
      this.programTemplate.isDirectPayProgram &&
      this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
    ) {
      this.setSubtotals();
    } else {
      setSingleValueForTasField('CARD_PRE_APPROVED_TOTAL', null, this.applicationFields);
      setSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', null, this.applicationFields);
      setSingleValueForTasField(
        this.approvedTotalString,
        this.setRequestedTotalAmount,
        this.applicationFields
      );
    }

    await this.saveTask.perform();
  }

  submitApplication = async () => {
    await this.prepareTotalsMiscFieldsAndSave();
    if (!this.canSubmitApplication) {
      return;
    }
    try {
      await this.saveAgreementsForConditions.perform();
      await this.submitTask.perform();
    } catch (error) {
      console.error('Failed to submit application:', error);
    }
  };

  submitTask = dropTask(async () => {
    this.hasSubmitted = true;
    this.submitError = null;
    try {
      const limitsValidationResponse = await this.validateLimits.perform();
      if (!limitsValidationResponse.can_submit) {
        this.displayLimitsValidationError(limitsValidationResponse);
        return;
      }
      const appAdapter = this.store.adapterFor(
        'tas-application'
      ) as unknown as TasApplicationAdapter;
      // adapterMethod falls back to '' when typeClassification isn't recognised;
      // guard so we throw a clear error instead of calling undefined at runtime.
      const adapterMethodName = this.adapterMethod;
      const adapterMethod = adapterMethodName
        ? (appAdapter[adapterMethodName as keyof TasApplicationAdapter] as
            | ((application: TasApplicationModel) => Promise<unknown>)
            | undefined)
        : undefined;
      if (typeof adapterMethod !== 'function') {
        throw new Error(
          `No TAS application adapter method available for typeClassification="${this.typeClassification}"`
        );
      }
      await adapterMethod.call(appAdapter, this.application);
      switch (this.typeClassification) {
        case 'TAS.ProgramType.2':
          this.router.transitionTo('authenticated.tas.confirmation', {
            queryParams: { tasType: 'two', applicationId: this.application.id },
          });
          break;
        case 'TAS.ProgramType.3':
          this.router.transitionTo('authenticated.tas.confirmation', {
            queryParams: { tasType: 'three', applicationId: this.application.id },
          });
          break;
        default:
          this.router.transitionTo('authenticated.tas.dashboard');
      }
    } catch (error) {
      console.error('Failed to submit application:', error);
      this.submitError = error;
    }
  });

  validateLimits = dropTask(async () => {
    const results = await this.tuitionAssistance.validateAmountRequestedAgainstCurrentLimitsStatus(
      this.args.model.application,
      this.args.model.application.tasProgramInstance.employee
    );
    return results;
  });

  displayLimitsValidationError(validationResponse = {}) {
    // TODO: Eventually this message will be configurable via the program template.
    const reductionDisplay = this.application.isCreditBased
      ? // @ts-expect-error: not sure about this one
        `${validationResponse.reduction_needed} credits`
      : // @ts-expect-error: not sure about this one
        this.formatMoney(validationResponse.reduction_needed);
    this.submitError = `You have exceeded your employer's maximum limit. Please reduce your requested amount by ${reductionDisplay}.`;
  }

  formatMoney(cents: number) {
    const dollars = cents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  }

  saveTask = dropTask(async () => {
    this.instance.customFields = this.instanceCustomFields;
    this.instance.fields = this.instanceFields;
    this.application.fields = this.applicationFields;

    try {
      await this.application.save();
      await this.instance.save();
    } catch (error) {
      console.error('Failed to save application or instance:', error);
    }
  });

  switchToGuidedFlow = dropTask(async () => {
    try {
      await this.saveTask.perform();
      if (this.application.hasDirtyAttributes || this.instance.hasDirtyAttributes) {
        return;
      }
    } catch {
      return;
    }
    this.betaExperience.setProductEnabled('ta', true);
    const type = this.typeClassification;
    const appId = this.application.id;
    const routeMap: Record<string, string> = {
      'TAS.ProgramType.2': 'authenticated.tas.applications.type-two-guided.new',
      'TAS.ProgramType.3': 'authenticated.tas.applications.type-three-guided.new',
    };
    const route = routeMap[type];
    if (route) {
      this.router.transitionTo(route, appId);
    }
  });

  onTrash = () => {
    this.isDeleteApplicationOpen = true;
  };

  toggleDeleteApplication = () => {
    this.isDeleteApplicationOpen = !this.isDeleteApplicationOpen;
  };

  toggleDeleteCourse = (course: TasCourseModel | null) => {
    this.isDeleteCourseOpen = !this.isDeleteCourseOpen;
    if (course) {
      this.course = course;
    }
  };

  deleteApplication = dropTask(async () => {
    const appAdapter = this.store.adapterFor('tas-application') as unknown as TasApplicationAdapter;
    try {
      await appAdapter.abandonCourse(this.application);
      await this.store.request(
        abandonProgramRequest(
          this.instance.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole)
        )
      );
      // TAS.TODO: temporary routing until tas dashboard is complete
      this.router.transitionTo('authenticated.tas.dashboard');
    } catch (e) {
      console.error(e);
    }
  });

  deleteCourse = dropTask(async () => {
    if (!this.course) {
      return;
    }
    try {
      if (this.course.isNew) {
        await this.course.rollbackAttributes();
      } else {
        await this.course.destroyRecord();
      }
      this.isDeleteCourseOpen = false;
      this.course = null;
    } catch (e) {
      console.error(e);
    }
  });

  // TAS.TODO: need custom field validation
  get customFieldValidationErrors() {
    return [];
  }

  openDeleteScholarship = (scholarship: ReceivedScholarship) => {
    this.scholarshipToDelete = scholarship;
    this.isDeleteScholarshipOpen = true;
  };

  closeDeleteScholarship = () => {
    delete this.scholarshipToDelete;
    this.isDeleteScholarshipOpen = false;
  };

  deleteScholarship = () => {
    let existingScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED',
      this.application.fields
    );
    let updatedScholarships = existingScholarships.filter(
      (scholarship: FieldValue) =>
        (scholarship as Record<string, unknown>)?.scholarshipId !==
        this.scholarshipToDelete?.scholarshipId
    ) as ReceivedScholarship[];

    this.application.fields['SCHOLARSHIPS_RECEIVED'] = {
      values: updatedScholarships.filter((scholarship) => scholarship !== null),
    };
    this.application.save();
    this.isDeleteScholarshipOpen = false;
  };

  openEditScholarship = (scholarship: ReceivedScholarship) => {
    // Use the scholarshipId from the actual data, not the generated display ID
    this.scholarshipIdToEdit = (
      scholarship as ReceivedScholarship & { scholarshipId?: string }
    ).scholarshipId;
    // Open the scholarship drawer which will load all scholarships for editing
    this.isScholarshipOpen = true;
  };

  closeEditScholarship = () => {
    delete this.scholarshipToEdit;
    this.isEditScholarshipOpen = false;
  };

  updateName = (value: string) => {
    this.scholarshipToEdit!.scholarshipName = value;
  };

  updateAmount = (value: string) => {
    this.scholarshipToEdit!.scholarshipAmount = currencyStringToNumber(value);
  };

  checkForMissingFields = async () => {
    if (
      (this.scholarshipToEdit?.scholarshipName === '' && this.scholarshipName.required) ||
      (this.scholarshipToEdit?.scholarshipAmount === 0 && this.scholarshipAmount.required)
    ) {
      this.scholarshipEditError = 'A required field is missing';
    } else {
      this.scholarshipEditError = '';
    }
  };

  updateScholarship = async () => {
    await this.checkForMissingFields();
    if (this.scholarshipEditError !== '') {
      return;
    }
    const updatedScholarships = this.scholarships.map((scholarship) =>
      scholarship.id === this.scholarshipToEdit?.id
        ? {
            scholarshipName: this.scholarshipToEdit.scholarshipName,
            scholarshipAmount: this.scholarshipToEdit.scholarshipAmount,
          }
        : scholarship
    );
    this.applicationFields['SCHOLARSHIPS_RECEIVED'] = {
      values: updatedScholarships,
    };
    delete this.scholarshipToEdit;
    this.closeEditScholarship();
  };

  getValue = (values: string[] | boolean[]): string => {
    return values[0]?.toString() || '';
  };

  getAllValuesForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    return getAllValuesForTasField(
      fieldName as keyof TASProgramInstanceModelFieldsSignature,
      this.instanceFields
    ) as string[];
  };

  getSingleValueForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    const values = this.getAllValuesForField(fieldName);
    return values[0] || '';
  };

  updateValueForField = (
    fieldName: FieldName,
    programField: FormField,
    value: string[] | string | number,
    event?: Event | undefined
  ) => {
    let valueToSet = Array.isArray(value) ? value[0] : value;
    if (getInputmaskAlias(event) === 'currency') {
      valueToSet = Number(getInputmaskUnmaskedValue(event)) * 100;
    }
    if (programField.type === 'number') {
      valueToSet = Math.round(Number(valueToSet));
    }
    if (programField.type === 'date' && typeof valueToSet === 'string') {
      valueToSet = normalizeDate(valueToSet as string);
    }
    const fields =
      programField.fieldOrigination === 'instance' ? this.instanceFields : this.applicationFields;
    // @ts-expect-error: fieldName
    setSingleValueForTasField(fieldName, valueToSet, fields);
  };

  setPaymentMethod = (value: string) => {
    this.paymentMethod = value;

    setSingleValueForTasField(
      'PAYMENT_TYPE_FOR_DIRECT_PAY',
      this.paymentMethod,
      this.applicationFields
    );
  };

  closePaymentMethodModal = () => {
    this.showPaymentMethodModal = false;
    // Save the application to persist the payment method change
    this.saveTask.perform();
  };

  initializePaymentMethod() {
    if (!this.programTemplate.isDirectPayProgram) return;

    const method = getSingleValueForTasField(
      'PAYMENT_TYPE_FOR_DIRECT_PAY',
      this.applicationFields,
      ''
    );
    // Set the tracked property for UI display (default was already set in constructor)
    this.paymentMethod = method || 'TAS.AssistanceManner.COURSE_CASH';
  }

  setSubtotals() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.application;

    const courseCashTotalField = this.isTypeTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = this.isTypeTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';

    setSingleValueForTasField(
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships,
      this.applicationFields
    );
    setSingleValueForTasField(
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal,
      this.applicationFields
    );
  }

  inputClasses = { base: 'w-full mb-6' };

  <template>
    <TourTrigger @tourId="course-cash-application" @autoStart={{true}} @delay={{500}} />
    <VStack>
      <Header class="tio-row-between">
        {{! TAS.TODO: temporary routing until tas dashboard is complete }}
        <LinkTo
          class="flex justify-left items-center gap-4 text-sm"
          @route="authenticated.tas.dashboard"
        >
          <PhCaretLeft @size="16" />
          {{t "dashboard"}}
        </LinkTo>
        <div class="tio-row-loose">
          <button
            type="button"
            class="text-xs tio-fg-muted underline hover:tio-fg-secondary cursor-pointer"
            data-test-id="standard-flow-toggle"
            disabled={{this.switchToGuidedFlow.isRunning}}
            {{on "click" this.switchToGuidedFlow.perform}}
          >
            {{t "tas.guided.try_guided_view"}}
          </button>
          <Button
            data-test-save-application
            @intent="primary"
            @appearance="outlined"
            @isRunning={{this.saveTask.isRunning}}
            {{on "click" this.saveTask.perform}}
          >
            {{t "common.save"}}
          </Button>
          <Button
            @intent="danger"
            @appearance="outlined"
            data-test-delete-application
            {{on "click" this.onTrash}}
          >
            <PhTrash @size="24" />
          </Button>
        </div>
      </Header>

      <HStack class="items-start gap-2">
        <VStack class="w-full lg:basis-2/3">
          <Section>
            <:header>
              <div>{{t "tas.application.type_two.new.application"}}</div>
              <CourseCashTag
                @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                @size="sm"
                class="font-semibold"
              >
                <:beforeTag>
                  <span>
                    {{t "common.tas.course_cash.eligible_for"}}
                  </span>
                </:beforeTag>
              </CourseCashTag>
            </:header>
            <:body>
              <div class="tio-row-flush text-sm">
                <span class="text-error-400 text-xl">*</span>
                {{t "tas.application.type_two.new.required_items"}}
              </div>
              <p>{{t "tas.application.type_two.new.expense_type_reminder"}}</p>
              <div class="tio-row-start-between w-full py-4">
                {{#if this.startDateConfig.visible}}
                  <div class="w-5/12">
                    <label for="start" class="tio-fg-secondary font-semibold">
                      {{capitalizeWords this.startDateLabel}}
                      {{#if this.startDateConfig.required}}
                        <span class="text-error-400">*</span>
                      {{/if}}
                    </label>
                    <div class="max-w-60">
                      <div class="relative">
                        {{! @glint-expect-error: EmberFlatpickr type overrides}}
                        <EmberFlatpickr
                          @date={{this.startDate}}
                          @onChange={{this.saveStartDate}}
                          @dateFormat="Y-m-d"
                          @altInput={{true}}
                          @altFormat="F j, Y"
                          @disableMobile={{true}}
                          data-test="start"
                          placeholder="mm/dd/yyyy"
                          class="tio-form-input-lg"
                        />
                        <div
                          class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                        >
                          <PhCalendarBlank @size="20" />
                        </div>
                      </div>
                      <div>
                        {{#each
                          (errorsForValibotField
                            "dateEntered"
                            schemaErrors=this.startDateValidationErrors
                            touched=this.touched
                          )
                          as |error|
                        }}
                          <Text @variant="error">{{error}}</Text>
                        {{/each}}
                      </div>
                    </div>
                  </div>
                {{/if}}
                {{#if this.endDateConfig.visible}}
                  <div class="w-5/12">
                    <label for="end" class="tio-fg-secondary font-semibold">
                      {{capitalizeWords this.endDateLabel}}
                      {{#if this.endDateConfig.required}}
                        <span class="text-error-400">*</span>
                      {{/if}}
                    </label>
                    <div class="max-w-60 relative">
                      {{! @glint-expect-error: EmberFlatpickr type overrides}}
                      <EmberFlatpickr
                        @date={{this.endDate}}
                        @onChange={{this.saveEndDate}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        @disableMobile={{true}}
                        data-test="end"
                        placeholder="mm/dd/yyyy"
                        class="tio-form-input-lg"
                      />
                      <div
                        class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                      >
                        <PhCalendarBlank @size="20" />
                      </div>
                    </div>
                  </div>
                {{/if}}
              </div>
              {{#unless this.eligibility}}
                <AlertBanner
                  @intent="warning"
                  @title={{t
                    "start_date_eligibility.errorHeader"
                    startEndSubmission=this.lockStatus
                  }}
                  data-test-eligibility-error
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{t "start_date_eligibility.errorText"}}
                  </p>
                </AlertBanner>
              {{/unless}}

              {{#unless this.eligibleBasedOnWaitingPeriod}}
                <AlertBanner
                  @intent="warning"
                  @title={{t "tuition_assistance.program_eligibility_warnings.dashboard_title"}}
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{t
                      "tuition_assistance.program_eligibility_warnings.invalid_start_date"
                      time=this.eligibilityWaitingPeriod
                      date=this.dateEligible
                      htmlSafe=true
                    }}
                  </p>
                </AlertBanner>
              {{/unless}}

              {{#unless this.isActiveParticipant}}
                <AlertBanner
                  @intent="warning"
                  @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                  class="my-4"
                >
                  <p class="text-sm">
                    {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                  </p>
                </AlertBanner>
              {{/unless}}
              <Divider class="my-2" />
              <div class="tio-row-flush w-full py-4">
                <div class="w-1/2">
                  <h2 class="tio-fg-secondary font-semibold">{{t
                      "tas.application.type_two.new.name"
                    }}
                    <span class="text-error-400">*</span>
                  </h2>
                </div>
                <div class="w-1/2">
                  <label for="application-name" class="sr-only">
                    {{t "tas.application.type_two.new.name"}}!!
                  </label>
                  <Input
                    @size="lg"
                    data-test-application-name
                    id="application-name"
                    @value={{getSingleValueForTasField
                      "APPLICATION_NAME"
                      @model.application.fields
                    }}
                    @onChange={{this.updateAppName}}
                  />
                </div>
              </div>
              <Divider class="mb-4" />

              <div class="tio-col">
                {{#each this.programFormFieldsForTypeTwoAndThree as |programField|}}
                  {{#if (this.isSchoolSelectProgramField programField.name)}}
                    <SchoolSelect
                      @programTemplate={{this.programTemplate}}
                      @labelClass="!text-gray-600 font-semibold w-1/2"
                      @selected={{this.selectedSchool}}
                      @onChange={{this.didSelectSchool}}
                      class="mb-2 tio-row-between flex-wrap gap-y-4 [&>div]:w-1/2 [&>div]:border [&>div]:border-default-400 [&>div]:rounded-lg"
                    />
                    {{#if this.isLpnSchoolSelected}}
                      <AlertBanner
                        @intent="info"
                        @title={{t "lpn.application_banner.title"}}
                        class="my-4"
                      >
                        <p class="text-sm">
                          {{t "lpn.application_banner.body"}}
                        </p>
                      </AlertBanner>
                    {{/if}}
                    <Divider class="my-4" />
                  {{else}}
                    <ProgramFormField
                      @name={{programField.name}}
                      @programTemplate={{this.instance.tasProgramTemplate}}
                      @labelClass="!text-gray-600 font-semibold w-1/2"
                      class="tio-row-between flex-wrap gap-y-4 [&>label]:w-1/2 [&>div]:w-1/2
                        {{if (eq programField.type 'date') '[&>div]:w-1/4' '[&>div]:w-1/2'}}"
                      as |field|
                    >
                      {{#if field.isFieldOptionPicklist}}
                        <ReSelect
                          data-test={{field.name}}
                          @items={{field.fieldOptions}}
                          @selected={{this.getSingleValueForField field.name}}
                          @onChange={{fn this.updateValueForField field.name programField}}
                          @disabled={{programField.disabled}}
                          @clearable={{false}}
                          @placeholder="Select an option"
                        />
                      {{else if (eq programField.mask "currency")}}
                        <Input
                          data-test={{field.name}}
                          id={{field.inputId}}
                          @value={{divide
                            (or
                              (Number (getSingleValueForTasField field.name this.instanceFields)) 0
                            )
                            100
                          }}
                          @onChange={{fn this.updateValueForField field.name programField}}
                          disabled={{programField.disabled}}
                          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2"}}
                        />
                      {{else if (eq programField.type "date")}}
                        <span class="w-1/2 relative block">
                          <EmberFlatpickr
                            {{! @glint-expect-error: FieldValue type is broader than DateOption }}
                            @date={{or
                              (getSingleValueForTasField field.name this.instanceFields)
                              ""
                            }}
                            @onChange={{fn this.updateDateFieldValue field.name}}
                            @dateFormat="Y-m-d"
                            @altInput={{true}}
                            @altFormat="F j, Y"
                            @disableMobile={{true}}
                            placeholder="mm/dd/yyyy"
                            data-test={{field.name}}
                            disabled={{programField.disabled}}
                            class="tio-form-input-lg"
                          />
                          <div
                            class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                          >
                            <PhCalendarBlank @size="20" />
                          </div>
                        </span>
                      {{else}}
                        <Input
                          data-test={{field.name}}
                          id={{field.inputId}}
                          @value={{this.inputValueForField field.name programField}}
                          @onChange={{fn this.updateValueForField field.name programField}}
                          type={{programField.type}}
                          disabled={{programField.disabled}}
                          {{!-- @errors={{errorsForField field.name schemaErrors=this.formValidationErrors}} --}}
                        />
                      {{/if}}
                      <Divider class="mb-4" />
                    </ProgramFormField>
                  {{/if}}
                {{/each}}
              </div>

              {{#if this.showGPAField}}
                <div class="tio-row-between flex-wrap gap-y-4">
                  <label
                    for="grade-point-average"
                    class="font-semibold block mb-1 text-balance tio-fg-secondary"
                  >
                    {{t "tas.application.grade_point_average"}}
                    <span class="text-error-400">*</span>
                  </label>
                  <Input
                    @size="lg"
                    type="number"
                    min="0"
                    max="4.0"
                    step="0.01"
                    data-test-grade-point-average
                    id="grade-point-average"
                    @value={{String
                      (or
                        (getSingleValueForTasField "GRADE_POINT_AVERAGE" @model.application.fields)
                        0
                      )
                    }}
                    class="w-1/2!"
                    @onChange={{this.setGPA}}
                  />
                  <Divider class="mb-4" />
                </div>
              {{/if}}
              <TuitionAssistanceFormsCustomFieldsForProvince
                @province="EMPLOYEE"
                @customFields={{this.customEmployeeFields}}
                @didUpdateFields={{this.didUpdateEmployeeCustomFields}}
                @formValidationErrors={{this.customFieldValidationErrors}}
                @fieldContainerClass="mb-4"
                @programTemplate={{this.programTemplate}}
              />
              <TuitionAssistanceFormsCustomFieldsForProvince
                @province="PROGRAM"
                @customFields={{this.customProgramFields}}
                @didUpdateFields={{this.didUpdateProgramCustomFields}}
                @formValidationErrors={{this.customFieldValidationErrors}}
                @fieldContainerClass="mb-4"
                @programTemplate={{this.programTemplate}}
              />
              <TuitionAssistanceFormsCustomFieldsForProvince
                @province="APPLICATION"
                @customFields={{this.customApplicationFields}}
                @didUpdateFields={{this.didUpdateApplicationCustomFields}}
                @formValidationErrors={{this.customFieldValidationErrors}}
                @fieldContainerClass="mb-4"
                @programTemplate={{this.programTemplate}}
              />
            </:body>
          </Section>
          <Section id="education-section">
            <:header>
              <div class="tio-row-flush">
                <p>{{this.expenseTypeField}}</p>
                <span class="text-error-400 text-xl">*</span>
              </div>
            </:header>
            <:actions>
              <div
                class="rounded-md transition-all duration-300
                  {{if
                    (eq this.highlightedSection 'education-section')
                    'ring-4 ring-violet-500 ring-offset-2'
                  }}"
              >
                <Button
                  @intent="primary"
                  data-test-add-education-drawer
                  {{on "click" (fn this.openEducationDrawer null)}}
                  class="add-education-drawer-button"
                >
                  <PhPlus @size="24" class="mr-2" />
                  {{or this.expenseTypeField (t "tas.application.type_two.new.education")}}
                </Button>
              </div>
            </:actions>
            <:body>
              <p>{{t "tas.application.type_two.new.expense_type_description"}}</p>
              <ActionableList @striped={{false}} as |l|>
                {{#each @model.application.activeCourses as |course|}}
                  <l.Row>
                    <l.Term class="md:w-1/3">{{course.courseName}}</l.Term>
                    <l.Details>
                      <l.Item class="justify-self-center md:justify-self-start">
                        {{#if @model.application.isCreditBased}}
                          {{course.courseCredit}}
                          {{t "common.credits"}}
                        {{else}}
                          {{safeFormatNumber
                            (divide course.courseTotal 100)
                            style="currency"
                            currency="USD"
                          }}
                        {{/if}}
                      </l.Item>
                      <l.Item
                        class="justify-self-center md:justify-self-start font-semibold"
                      >{{course.courseGrade}}</l.Item>
                    </l.Details>
                    <l.Action>
                      <button
                        type="button"
                        data-test-delete-course
                        {{on "click" (fn this.toggleDeleteCourse course)}}
                      >
                        <PhXCircle @size="24" />
                      </button>
                      <button
                        type="button"
                        data-test-edit-education-drawer
                        {{on "click" (fn this.openEducationDrawer course)}}
                      >
                        <PhNotePencil @size="24" />
                      </button>
                    </l.Action>
                  </l.Row>
                {{/each}}
              </ActionableList>
            </:body>
          </Section>
          {{! Add Scholarships }}
          {{#if this.programTemplate.scholarshipsAreVisible}}
            <Section>
              <:header>
                <div class="tio-row-between">
                  <p>
                    {{this.scholarshipReceived.label}}
                    <span class="text-normal">{{t "tas.new.scholarships.optional"}}</span>
                  </p>

                </div>
              </:header>
              <:actions>
                <Button
                  @intent="primary"
                  data-test-add-scholarship-drawer
                  {{on "click" this.toggleScholarshipDrawer}}
                >
                  <PhPlus @size="24" class="mr-2" />
                  {{this.scholarshipReceived.label}}
                </Button>
              </:actions>
              <:body>
                <p>
                  {{t "tas.new.scholarships.please_enter_any_scholarships"}}
                </p>
                <ActionableList @striped={{false}} as |l|>
                  {{#each this.scholarships as |scholarship|}}
                    <l.Row>
                      <l.Term class="md:w-1/2">{{scholarship.scholarshipName}}</l.Term>
                      <l.Details>
                        <l.Item class="justify-self-end md:justify-self-start">
                          {{safeFormatNumber
                            (divide scholarship.scholarshipAmount 100)
                            style="currency"
                            currency="USD"
                          }}
                        </l.Item>
                      </l.Details>
                      <l.Action>
                        <button type="button" data-test-delete-scholarship>
                          <PhXCircle
                            @size="24"
                            class="tio-fg-secondary"
                            {{on "click" (fn this.openDeleteScholarship scholarship)}}
                          />
                        </button>
                        <button type="button" data-test-edit-scholarship>
                          <PhNotePencil
                            @size="24"
                            class="tio-fg-secondary"
                            {{on "click" (fn this.openEditScholarship scholarship)}}
                          />
                        </button>
                      </l.Action>
                    </l.Row>
                  {{/each}}
                </ActionableList>
              </:body>
            </Section>
          {{/if}}
          {{! Upload Attachments}}
          {{#if this.shouldShowAttachments}}
            <Section id="attachments-section">
              <:header>
                {{t "common.attachments"}}
                {{#if this.isAttachmentRequired}}
                  <span class="text-error-400 text-xl">*</span>
                {{/if}}
              </:header>
              <:body>
                <VStack>
                  <div
                    class="transition-all duration-300 rounded-lg
                      {{if
                        (eq this.highlightedSection 'attachments-section')
                        'ring-2 ring-violet-400 ring-offset-2'
                      }}"
                  >
                    <UploadAttachments
                      @programTemplate={{this.programTemplate}}
                      @classification={{this.attachmentClassificationString}}
                      @attachable={{@model.application}}
                      @attachableType="tasApplication"
                      @useDropzone={{true}}
                      @instructions={{this.attachmentsListInstructions}}
                      @items={{this.attachmentsListItems}}
                      @hideDiscarded={{true}}
                      {{!-- @disabled={{this.disableAttachmentUpload}} --}}
                    />
                  </div>
                  {{#if this.attachmentsListItems.length}}
                    <div
                      class="mt-3 rounded-md tio-bg-info-soft border tio-border-info p-3 text-sm tio-fg-info tio-row-top-tight"
                    >
                      <PhLightbulb @size={{18}} class="shrink-0 mt-0.5" />
                      <p>
                        {{t "tas.guided.typed_attachments_note"}}
                        <button
                          type="button"
                          class="ml-1 font-medium underline hover:tio-fg-info-body"
                          disabled={{this.switchToGuidedFlow.isRunning}}
                          {{on "click" this.switchToGuidedFlow.perform}}
                        >
                          {{t "tas.guided.try_guided_view"}}
                        </button>
                      </p>
                    </div>
                  {{/if}}
                </VStack>
              </:body>
            </Section>
          {{/if}}
        </VStack>
        <VStack class="w-full lg:basis-1/3 md:w-1/2!">
          <Section class="w-full sticky top-0">
            <:header>{{t "tas.benefits_summary.title"}}</:header>
            <:body>
              <LimitsSummary
                @application={{@model.application}}
                @employee={{@model.programInstance.employee}}
                @labelClass="col-span-5"
                @valueClass="col-span-2 text-violet-800"
                @paymentMethod={{this.paymentMethod}}
                @shouldFetchLimits={{this.shouldFetchLimits}}
                class="my-4"
                as |Summary|
              >
                <div class="w-full grid grid-cols-7 gap-2 mt-3">
                  {{#unless Summary.isUnlimitedMaximumAnnualBenefit}}
                    <Summary.MaxAnnualBenefitLineItem />
                  {{/unless}}

                  {{#if Summary.hasLimitsErrorForEmployee}}
                    <Summary.RemainingBenefitLineItem />
                  {{/if}}
                  <Summary.ApplicationAmountRequestedWithoutScholarshipsLineItem
                    @valueClass="text-tio-gray-800 col-span-2"
                  />
                  {{#if (and this.programTemplate.scholarshipsAreVisible (not Summary.isCredits))}}
                    <Summary.ScholarshipAmountLineItem
                      @valueClass="text-tio-gray-800 !font-normal col-span-2"
                    />
                  {{/if}}
                  <Summary.LimitsAlertForEmployeeIfApplicable class="text-sm col-span-full" />
                  <ReimbursementCalculationIfApplicable @application={{@model.application}} />
                  <Divider class="my-2 col-span-7" />

                  {{#if this.programTemplate.isDirectPayProgram}}
                    <div class="col-span-7 tio-row-between">
                      <p>{{t "tas.payment_method.default"}}</p>
                      <Button
                        {{on "click" (fn (mut this.showPaymentMethodModal) true)}}
                        @appearance="minimal"
                        class="course-cash-pencil"
                      >
                        <PhPencil @size="20" />
                      </Button>
                    </div>
                    <Summary.ApplicationCourseCashSubtotalLineItem
                      @labelClass="ml-6 col-span-5"
                      @valueClass="text-tio-gray-800 !font-normal col-span-2"
                    >
                      <CourseCashTag @isDirectPayProgram={{true}} @size="sm" />
                    </Summary.ApplicationCourseCashSubtotalLineItem>
                    <Summary.ApplicationAllOtherExpensesSubtotalLineItem
                      @labelClass="ml-6 col-span-5"
                      @valueClass="text-tio-gray-800 !font-normal col-span-2"
                    >
                      <ReimbursementPopoverTag @size="sm" />
                    </Summary.ApplicationAllOtherExpensesSubtotalLineItem>
                    <Divider class="my-2 col-span-7" />
                  {{/if}}

                  {{#if this.programTemplate.calculateTotalByGPA}}
                    <Summary.ApplicationAmountBasedOnGPALineItem />
                  {{else}}
                    <Summary.ApplicationAmountRequestedLineItem />
                  {{/if}}
                </div>

                {{! Required Items Section - ASIDE }}
                <section class="my-5">
                  <header
                    class="border-y tio-border-default bg-tio-gray-25 tio-row-tight py-2 px-5 text-sm font-medium tracking-wide -mx-4"
                  >
                    <PhClipboardText @size="32" />
                    <span>{{t "tas.benefits_summary.required_items"}}</span>
                  </header>
                  <button
                    type="button"
                    class="tio-row mt-4 cursor-pointer group bg-transparent border-0 p-0 w-full text-left"
                    aria-label={{this.expenseTypeField}}
                    {{on "click" (fn this.scrollTo "education-section")}}
                  >
                    {{#if @model.application.activeCourses.length}}
                      <PhCheckCircle @size="24" @weight="fill" class="tio-fg-brand shrink-0" />
                    {{else}}
                      <PhCircle
                        @size="24"
                        class="text-tio-gray-200 group-hover:tio-fg-brand shrink-0 transition-colors"
                      />
                    {{/if}}
                    <span
                      class="group-hover:tio-fg-brand group-hover:underline transition-colors"
                    >{{this.expenseTypeField}}</span>
                  </button>
                  {{#if this.isAttachmentRequired}}
                    <div>
                      <div class="flex flex-row justify-between items-center pt-4">
                        <button
                          type="button"
                          class="tio-row cursor-pointer group bg-transparent border-0 p-0 text-left"
                          aria-label={{t "tas.benefits_summary.attachments"}}
                          {{on "click" (fn this.scrollTo "attachments-section")}}
                        >
                          {{#if this.hasRequiredAttachments}}
                            <PhCheckCircle
                              @size="24"
                              @weight="fill"
                              class="tio-fg-brand shrink-0"
                            />
                          {{else}}
                            <PhCircle
                              @size="24"
                              class="text-tio-gray-200 group-hover:tio-fg-brand shrink-0 transition-colors"
                            />
                          {{/if}}
                          <span
                            class="group-hover:tio-fg-brand group-hover:underline transition-colors"
                          >{{t "tas.benefits_summary.attachments"}}</span>
                        </button>
                        <div>
                          <UploadAttachments
                            @programTemplate={{this.programTemplate}}
                            @classification={{this.attachmentClassificationString}}
                            @attachable={{@model.application}}
                            @attachableType="tasApplication"
                            @useDropzone={{false}}
                            @iconOnlyUpload={{true}}
                            @hideInstructions={{true}}
                            @hideAttachments={{true}}
                            {{!-- @disabled={{this.disableAttachmentUpload}} --}}
                          />
                        </div>
                      </div>
                      {{#if this.attachmentsListItems.length}}
                        <div class="tio-row -mt-1">
                          {{#if this.hasRequiredAttachments}}
                            <PhCheckCircle
                              @size="24"
                              @weight="fill"
                              class="tio-fg-brand shrink-0"
                            />
                          {{else}}
                            <PhCircle @size="24" class="text-tio-gray-200 shrink-0" />
                          {{/if}}
                          <p>{{join this.attachmentsListItems ", "}}</p>
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}

                  {{#if this.isLpnSchoolSelected}}
                    <div class="tio-row-top">
                      <Checkbox
                        @label={{t "lpn.discount_checkbox.label"}}
                        @checked={{@model.application.lpnDiscountActivated}}
                        @onChange={{this.toggleLpnDiscountActivated}}
                      />
                      <button
                        type="button"
                        class="tio-inline-row-center tio-fg-disabled"
                        title={{t "lpn.discount_checkbox.tooltip"}}
                        aria-label={{t "lpn.discount_checkbox.tooltip"}}
                      >
                        <PhInfo @size="16" />
                      </button>
                    </div>
                  {{/if}}

                  {{#if this.conditionsForSubmit.length}}
                    <AcceptConditions
                      @conditions={{this.conditionsForSubmit}}
                      @onChange={{this.didUpdateAcceptedConditions}}
                    />
                  {{/if}}
                </section>

                <div class="py-4 tio-stack-center">
                  <Button
                    @isRunning={{this.submitTask.isRunning}}
                    @isDisabled={{or
                      Summary.isDisabledSubmitForEmployee
                      (not this.canSubmitApplication)
                    }}
                    @intent="primary"
                    data-test-submit-application
                    {{on "click" this.submitApplication}}
                  >
                    {{t "tas.benefits_summary.submit_application"}}
                  </Button>
                  <p class="text-sm font-light text-center text-pretty">
                    {{t "tas.benefits_summary.helper_text"}}
                  </p>
                  {{#if this.submitError}}
                    <ErrorMessages
                      @showErrors={{true}}
                      @error={{this.submitError}}
                      class="text-sm"
                    />
                  {{/if}}
                </div>
              </LimitsSummary>
            </:body>
          </Section>
        </VStack>
      </HStack>
    </VStack>
    {{#if this.isEducationOpen}}
      <AddEducationDrawer
        @isOpen={{this.isEducationOpen}}
        @onClose={{this.closeEducationDrawer}}
        @application={{@model.application}}
        @course={{this.course}}
        @header={{this.expenseTypeField}}
        @paymentMethod={{this.paymentMethod}}
        @setPaymentMethod={{this.setPaymentMethod}}
      />
    {{/if}}
    <AddScholarshipsDrawer
      @isOpen={{this.isScholarshipOpen}}
      @onClose={{this.toggleScholarshipDrawer}}
      @application={{@model.application}}
      @scholarshipIdToEdit={{this.scholarshipIdToEdit}}
    />

    {{! Benefits Summary Window}}

    {{! Delete Application Modal }}
    <Modal
      @isOpen={{this.isDeleteApplicationOpen}}
      @onClose={{this.toggleDeleteApplication}}
      data-test-delete-application-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_application"}}</:header>
      <:body>
        <p>{{t "tas.application.type_two.new.confirm_application_delete"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteApplication.isRunning}}
          @onCancel={{this.toggleDeleteApplication}}
          @onSubmit={{this.deleteApplication.perform}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isDeleteCourseOpen}}
      @onClose={{fn this.toggleDeleteCourse null}}
      data-test-delete-course-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_course"}}</:header>
      <:body>
        <p>{{t "tas.application.type_two.new.confirm_delete_expense"}}:</p>
        <p class="font-semibold px-2">{{this.course.courseName}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteCourse.isRunning}}
          @onCancel={{fn this.toggleDeleteCourse null}}
          @onSubmit={{this.deleteCourse.perform}}
        />
      </:footer>
    </Modal>
    {{! Delete Scholarship Modal }}
    <Modal
      @isOpen={{this.isDeleteScholarshipOpen}}
      @onClose={{this.closeDeleteScholarship}}
      data-test-delete-scholarship-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_scholarship"}}</:header>
      <:body>
        {{#let this.scholarshipToDelete as |scholarship|}}
          {{#if scholarship}}
            <p>{{t "tas.application.type_two.new.confirm_scholarship_delete"}}</p>
            <div class="p-8 tio-flex-between w-2/3">
              <p class="font-medium">{{scholarship.scholarshipName}}</p>
              <p class="font-medium">{{safeFormatNumber
                  (divide scholarship.scholarshipAmount 100)
                  style="currency"
                  currency="USD"
                }}</p>
            </div>
          {{/if}}
        {{/let}}
      </:body>
      <:footer>
        <Button @appearance="minimal" class="mr-4" {{on "click" this.closeDeleteScholarship}}>
          {{t "common.cancel"}}
        </Button>
        <Button @intent="danger" {{on "click" this.deleteScholarship}}>
          {{t "common.delete"}}
        </Button>
      </:footer>
    </Modal>
    {{! Edit Scholarship Modal }}
    <Modal
      @isOpen={{this.isEditScholarshipOpen}}
      @onClose={{this.closeEditScholarship}}
      data-test-edit-scholarship-modal
    >
      <:header>{{t "tas.application.type_two.new.edit_scholarship"}}</:header>
      <:body>
        {{#if this.scholarshipToEdit}}
          <div class="tio-col-flush w-full">
            <label for="scholarship_name4" class="font-semibold tio-fg-secondary">
              {{this.scholarshipName.label}}
              {{#if this.scholarshipName.required}}
                <span class="text-error-400">*</span>
              {{/if}}
            </label>
            <Input
              @value={{this.scholarshipToEdit.scholarshipName}}
              @classes={{this.inputClasses}}
              @onChange={{this.updateName}}
              @isRequired={{true}}
              data-test-edit-scholarship-name
            />
            <label for="scholarship_amount4" class="font-semibold tio-fg-secondary">
              {{this.scholarshipAmount.label}}
              {{#if this.scholarshipAmount.required}}
                <span class="text-error-400">*</span>
              {{/if}}
            </label>
            <Input
              @value={{safeFormatNumber
                (divide this.scholarshipToEdit.scholarshipAmount 100)
                style="currency"
                currency="USD"
              }}
              @classes={{this.inputClasses}}
              @onChange={{this.updateAmount}}
              @isRequired={{true}}
              data-test-edit-scholarship-amount
              {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
            />
            <p class="text-danger">{{this.scholarshipEditError}}</p>
          </div>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onCancel={{this.closeEditScholarship}}
          @onSubmit={{this.updateScholarship}}
        />
      </:footer>
    </Modal>

    <PaymentMethodAndSubtotals
      @show={{this.showPaymentMethodModal}}
      @cancel={{this.closePaymentMethodModal}}
      @application={{@model.application}}
      @paymentMethod={{this.paymentMethod}}
      @setPaymentMethod={{this.setPaymentMethod}}
    />
  </template>
}
